import { createApp } from 'vue'

import App from './App.vue'
import router from './router'
import store from './store'

const app = createApp(App)

const WOWHEAD_TOOLTIP_SELECTORS = [
    '#wowhead_tooltip',
    '#wowhead-tooltip',
    '.wowhead-tooltip',
    '.wowhead_tooltip',
    '[id^="wowhead_tooltip"]',
]

const wowheadPointerState = {
    sequence: 0,
    frameId: null,
    timeoutIds: [],
}

function isStandaloneWebApp() {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
}

function findWowheadTooltip() {
    return WOWHEAD_TOOLTIP_SELECTORS
        .map((selector) => document.querySelector(selector))
        .find(Boolean)
}

function clampTooltipPosition(value, size, max) {
    const padding = 10
    return Math.max(padding, Math.min(value, Math.max(padding, max - size - padding)))
}

function placeWowheadTooltip(clientX, clientY, touchMode = false) {
    const tooltip = findWowheadTooltip()
    if (!tooltip) return

    const offsetX = touchMode ? 12 : 22
    const offsetY = touchMode ? 14 : 22
    const rect = tooltip.getBoundingClientRect()
    const width = rect.width || 320
    const height = rect.height || 220
    const hasRoomRight = clientX + offsetX + width < window.innerWidth - 10
    const hasRoomBelow = clientY + offsetY + height < window.innerHeight - 10
    const preferredLeft = hasRoomRight ? clientX + offsetX : clientX - width - offsetX
    const preferredTop = hasRoomBelow ? clientY + offsetY : clientY - height - offsetY
    const left = clampTooltipPosition(preferredLeft, width, window.innerWidth)
    const top = clampTooltipPosition(preferredTop, height, window.innerHeight)

    tooltip.style.setProperty('position', 'fixed', 'important')
    tooltip.style.setProperty('left', `${left}px`, 'important')
    tooltip.style.setProperty('top', `${top}px`, 'important')
    tooltip.style.setProperty('right', 'auto', 'important')
    tooltip.style.setProperty('bottom', 'auto', 'important')
    tooltip.style.setProperty('transform', 'none', 'important')
    tooltip.style.setProperty('transition', 'none', 'important')
    tooltip.style.setProperty('pointer-events', 'none', 'important')
    tooltip.style.setProperty('z-index', '99999', 'important')
}

function scheduleWowheadTooltipPlacement(clientX, clientY, touchMode = false) {
    wowheadPointerState.sequence += 1
    const sequence = wowheadPointerState.sequence
    const place = () => {
        if (sequence !== wowheadPointerState.sequence) return
        placeWowheadTooltip(clientX, clientY, touchMode)
    }

    if (wowheadPointerState.frameId) {
        window.cancelAnimationFrame(wowheadPointerState.frameId)
    }
    wowheadPointerState.timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId))

    wowheadPointerState.frameId = window.requestAnimationFrame(() => {
        wowheadPointerState.frameId = null
        place()
    })
    wowheadPointerState.timeoutIds = [
        window.setTimeout(place, 35),
        window.setTimeout(place, 120),
    ]
}

function installWowheadTooltipPositioning() {
    document.addEventListener('mousemove', (event) => {
        if (!event.target.closest?.('a[data-wowhead]')) return
        scheduleWowheadTooltipPlacement(event.clientX, event.clientY, false)
    }, true)

    document.addEventListener('touchstart', (event) => {
        const target = event.target.closest?.('a[data-wowhead]')
        const touch = event.changedTouches?.[0]
        if (!target || !touch) return
        scheduleWowheadTooltipPlacement(touch.clientX, touch.clientY, true)
    }, true)
}

async function normalizeLegacyIosStartRoute() {
    const currentRoute = router.currentRoute.value
    if (!isStandaloneWebApp() || currentRoute.path !== '/raiding') {
        return
    }

    const navigationEntry = performance.getEntriesByType('navigation')[0]
    const isColdLaunch = !navigationEntry || navigationEntry.type === 'navigate'

    if (isColdLaunch) {
        await router.replace('/mythic-plus')
    }
}

app.use(store)
app.use(router)

try {
    store.dispatch('initialize')
} catch (error) {
    // Initialization must never block mounting; the app falls back to defaults.
    console.error('Store initialization failed', error)
}
installWowheadTooltipPositioning()

if (process.env.NODE_ENV === 'production') {
    store.dispatch('registerServiceWorker')
} else if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => registration.unregister())
    })
}

router.isReady().then(async () => {
    await normalizeLegacyIosStartRoute()
    app.mount('#app')
})
